<template>
  <div ref="root" class="simple-searchable-select simple-searchable-select--glass">
    <div ref="wrapper" class="simple-select-wrapper" :class="{ 'is-open': isOpen }">
      <input
        ref="input"
        v-model="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        class="simple-select-input"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen ? 'true' : 'false'"
        @focus="openDropdown"
        @input="handleInput"
        @keydown="handleKeydown"
        autocomplete="off"
      />
      <i
        class="bi bi-chevron-down simple-select-arrow"
        :class="{ rotated: isOpen }"
        aria-hidden="true"
        @click="toggleDropdown"
      ></i>
    </div>

    <Teleport to="body">
      <Transition name="simple-select-menu">
        <div
          v-if="isOpen"
          ref="menuPanel"
          class="simple-select-dropdown simple-select-dropdown--teleported"
          :style="menuStyle"
          role="listbox"
          @mousedown.stop
        >
          <div
            v-for="option in filteredOptions"
            :key="getOptionKey(option)"
            class="simple-select-option"
            :class="{ selected: isOptionSelected(option) }"
            role="option"
            :aria-selected="isOptionSelected(option) ? 'true' : 'false'"
            @mousedown.prevent="selectOption(option)"
          >
            {{ getOptionLabel(option) }}
            <i v-if="isOptionSelected(option)" class="bi bi-check simple-select-check" aria-hidden="true"></i>
          </div>
          <div v-if="filteredOptions.length === 0" class="simple-select-no-results" role="status">
            No results found
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'SimpleSearchableSelect',
  emits: ['input', 'update:modelValue', 'change'],
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select option...',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
  },
  data() {
    return {
      isOpen: false,
      searchText: '',
      selectedValue: this.modelValue ?? this.value,
      menuStyle: {},
    };
  },
  computed: {
    displayValue: {
      get() {
        if (this.isOpen && this.searchText !== null) {
          return this.searchText;
        }
        return this.getSelectedLabel();
      },
      set(value) {
        this.searchText = value;
      },
    },
    filteredOptions() {
      if (!this.searchText || this.searchText === this.getSelectedLabel()) {
        return this.options;
      }

      const search = this.searchText.toLowerCase();
      return this.options.filter((option) => {
        const label = this.getOptionLabel(option).toLowerCase();
        return label.includes(search);
      });
    },
  },
  watch: {
    value(newVal) {
      this.selectedValue = newVal;
    },
    modelValue(newVal) {
      this.selectedValue = newVal;
    },
    isOpen(open) {
      if (open) {
        this.$nextTick(() => {
          this.updateMenuPosition();
          this.$nextTick(() => this.updateMenuPosition());
          this.attachPositionListeners();
        });
      } else {
        this.detachPositionListeners();
      }
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside);
  },
  beforeUnmount() {
    this.detachPositionListeners();
    document.removeEventListener('mousedown', this.handleClickOutside);
  },
  methods: {
    getOptionKey(option) {
      return this.getOptionValue(option) ?? Math.random();
    },
    getOptionValue(option) {
      if (typeof option === 'object') {
        return option[this.optionValue];
      }
      return option;
    },
    getOptionLabel(option) {
      if (typeof option === 'object') {
        return option[this.optionLabel];
      }
      return option;
    },
    getSelectedLabel() {
      if (!this.selectedValue) return '';

      const selected = this.options.find(
        (option) => this.getOptionValue(option) === this.selectedValue
      );

      return selected ? this.getOptionLabel(selected) : this.selectedValue;
    },
    isOptionSelected(option) {
      return this.getOptionValue(option) === this.selectedValue;
    },
    getTriggerElement() {
      return this.$refs.input || this.$refs.wrapper;
    },
    updateMenuPosition() {
      const trigger = this.getTriggerElement();
      if (!trigger || !this.isOpen) return;

      const rect = trigger.getBoundingClientRect();
      const panel = this.$refs.menuPanel;
      const menuHeight = panel?.offsetHeight || 220;
      const gap = 3;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward =
        spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;

      this.menuStyle = {
        position: 'fixed',
        top: openUpward
          ? `${Math.max(8, rect.top - menuHeight - gap)}px`
          : `${rect.bottom + gap}px`,
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 140)}px`,
        zIndex: 10100,
      };
    },
    attachPositionListeners() {
      window.addEventListener('scroll', this.updateMenuPosition, true);
      window.addEventListener('resize', this.updateMenuPosition);
    },
    detachPositionListeners() {
      window.removeEventListener('scroll', this.updateMenuPosition, true);
      window.removeEventListener('resize', this.updateMenuPosition);
    },
    openDropdown() {
      if (this.disabled) return;
      this.isOpen = true;
      this.searchText = this.getSelectedLabel();
      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.select();
        }
      });
    },
    closeDropdown() {
      this.isOpen = false;
      this.searchText = '';
      this.menuStyle = {};
      this.detachPositionListeners();
    },
    toggleDropdown() {
      if (this.disabled) return;
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    selectOption(option) {
      const value = this.getOptionValue(option);
      this.selectedValue = value;
      this.searchText = '';
      this.closeDropdown();

      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },
    handleInput() {
      if (!this.isOpen) {
        this.openDropdown();
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeDropdown();
      } else if (event.key === 'Enter' && this.filteredOptions.length > 0) {
        this.selectOption(this.filteredOptions[0]);
      }
    },
    handleClickOutside(event) {
      const root = this.$refs.root;
      const panel = this.$refs.menuPanel;
      if (root?.contains(event.target) || panel?.contains(event.target)) {
        return;
      }
      this.closeDropdown();
    },
  },
};
</script>

<style src="@/components/styles/SimpleSearchableSelect.css"></style>
